import { Button, Loader, Popup, VText } from '@vake/ui-kit';
import { FC } from 'react';
import { Link } from 'react-router-dom';
import { useGetVesselPathfinderQuery } from '../requests/actor-mode';
import { SearchResult } from '../requests/search';
import { VakeClipboard } from '../utils/clipboard';
import { getPathfinder2Url } from '../utils/env';
import { mmsiToFlag } from '../utils/mmsi-to-flag';
import { SendToMattermost } from './Report/components/send-to-mattermost';
type Props = {
    activeResult: SearchResult | null;
    showPopup: boolean;
    setShowPopup: (show: boolean) => void;
};
export const SearchPopup: FC<Props> = ({ activeResult, showPopup, setShowPopup }) => {
    const {
        data: vesselPathfinders,
        isLoading: loadingPathfinders,
        isError: errorPathfinders,
        isSuccess: pathfindersSuccess,
    } = useGetVesselPathfinderQuery(activeResult?.mmsi ? Number(activeResult.mmsi) : undefined);
    return (
        <Popup tooglePopup={() => setShowPopup(false)} isOpen={showPopup}>
            <div className="flex flex-col gap-2">
                <div>
                    <VText>{activeResult?.mmsi ? mmsiToFlag(activeResult?.mmsi) : 'N/A'}</VText>
                    <div className="flex items-center"></div>
                    <div className="flex items-center">
                        <VText>Name: {activeResult?.name ? activeResult.name : 'N/A'}</VText>
                        {activeResult?.name && <VakeClipboard infoValue={activeResult.name} />}
                    </div>
                    <div className="flex items-center">
                        <VText>IMO: {activeResult?.imo ? activeResult.imo : 'N/A'}</VText>
                        {activeResult?.imo && <VakeClipboard infoValue={activeResult.imo.toString()} />}
                    </div>
                    <div className="flex items-center">
                        <VText>MMSI: {activeResult?.mmsi ? activeResult.mmsi : 'N/A'}</VText>
                        {activeResult?.mmsi && <VakeClipboard infoValue={activeResult.mmsi.toString()} />}
                    </div>
                </div>
                <div className="flex flex-col">
                    <VText>Pathfinder reports</VText>
                    {loadingPathfinders && (
                        <div className="flex justify-start">
                            <Loader size={16} />
                        </div>
                    )}
                    {errorPathfinders && <VText significance="secondary">Error loading pathfinder reports</VText>}
                    {pathfindersSuccess && (
                        <div className="max-h-60 overflow-auto">
                            {vesselPathfinders && vesselPathfinders.length > 0 ? (
                                vesselPathfinders.map((pathfinder, index) => {
                                    const pathfinderLink =
                                        pathfinder.url === 'none'
                                            ? `${getPathfinder2Url()}/${pathfinder.uuid}`
                                            : `/pathfinder/${pathfinder.url}`;
                                    return (
                                        <Link to={pathfinderLink} key={index}>
                                            <Button
                                                text={`${pathfinder.name}: ${
                                                    pathfinder.startTime?.toDateString() ?? 'N/A'
                                                } - ${pathfinder.endTime?.toDateString() ?? 'N/A'}`}
                                                variant="neutral"
                                            />
                                        </Link>
                                    );
                                })
                            ) : (
                                <VText significance="secondary">No pathfinder reports available</VText>
                            )}
                        </div>
                    )}
                    <Link to={`${getPathfinder2Url()}/create?mmsi=${activeResult?.mmsi}&name=${activeResult?.name}`}>
                        <Button text="Order Pathfinder" size="small" />
                    </Link>
                </div>
                <div>
                    <VText>Actions</VText>
                    <SendToMattermost
                        textOnButton={'Help me investigate this vessel'}
                        iconOnButton={undefined}
                        textToSend={`Help me investigate this vessel: ${activeResult?.name} (MMSI: ${activeResult?.mmsi}, IMO: ${activeResult?.imo})`}
                        successMessage={'We will help you investigate this vessel.'}
                        variant="neutral"
                        size="small"
                    />
                    <SendToMattermost
                        textOnButton={'Help me find a satellite image of this vessel'}
                        iconOnButton={undefined}
                        textToSend={`Help me find a satellite image of this vessel: ${activeResult?.name} (MMSI: ${activeResult?.mmsi}, IMO: ${activeResult?.imo})`}
                        successMessage={'We will help you find a satellite image of this vessel.'}
                        variant="neutral"
                        size="small"
                    />
                    <SendToMattermost
                        textOnButton={'Something is wrong with this vessel'}
                        iconOnButton={undefined}
                        textToSend={`Something is wrong with this vessel: ${activeResult?.name} (MMSI: ${activeResult?.mmsi}, IMO: ${activeResult?.imo})`}
                        successMessage={'We will update this vessel.'}
                        variant="neutral"
                        size="small"
                    />
                </div>
            </div>
        </Popup>
    );
};
